import { useEffect, useState } from 'react'
import {
  Button,
  IconVolumeOffDefault,
  IconVolumeOnDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  FullscreenContainer,
  Video,
  VideoAutoplay,
  VideoPlayback,
  useVideo,
} from '@mc/video'

type Props = {
  mediaUuid: string
  className?: string
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Hero video that starts muted on its own, with controls to stop it and to
 * turn sound on.
 *
 * Deliberately not `loop`: `@mc/video` implements looping in userland as
 * `onEnded -> play()`, and every one of those plays broadcasts on the singleton
 * channel regardless of `exemptSingleton` — which pauses the case-study player
 * further down the page once a visitor has started it.
 */
export const AutoplayVideo = ({ mediaUuid, className }: Props) => {
  const [autoPlay, setAutoPlay] = useState(false)

  useEffect(() => {
    if (prefersReducedMotion()) return undefined

    const timeout = setTimeout(() => setAutoPlay(true), 2000)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <div
      className={`mc-theme-light mc-corners--md mc-border--default mc-overflow--hidden ${className ?? ''}`}
    >
      <FullscreenContainer>
        <div className='mc-w-100 mc-h-100'>
          <Video mediaUuid={mediaUuid} muted fit='cover' exemptSingleton>
            {autoPlay && <VideoAutoplay muted />}
            <ToggleMute />
            <VideoPlayback
              kind='control'
              size='sm'
              className='position-absolute mc-m-3 mc-py-2 mc-px-3'
              style={{ right: 0, top: 0 }}
            />
          </Video>
        </div>
      </FullscreenContainer>
    </div>
  )
}

const ToggleMute = () => {
  const { muted, setMuted } = useVideo()
  const { t } = useTranslation('@mc/next')

  const Icon = muted ? IconVolumeOffDefault : IconVolumeOnDefault

  return (
    <Button
      kind='control'
      size='sm'
      onClick={() => setMuted(!muted)}
      className='position-absolute mc-m-3 mc-py-2 mc-px-3'
      style={{ left: 0, top: 0 }}
    >
      <Icon className='mc-mr-1' aria-hidden='true' />
      {muted
        ? t('forBusiness.heroVideo.unmute')
        : t('forBusiness.heroVideo.mute')}
    </Button>
  )
}
